import React, { useEffect, useMemo, useState } from "react";
import { motion, MotionConfig } from "framer-motion";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInstagram, faTiktok } from "@fortawesome/free-brands-svg-icons";
import {
  faArrowLeft,
  faBuilding,
  faChartLine,
  faLayerGroup,
  faLock,
  faMedal,
  faShoePrints,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { supabase } from "../supabaseClient";
import { workspaceNameSlug } from "../institutionWorkspaceSlug";
import { useAuth } from "../AuthContext";
import GymLiveDashboard from "./GymLiveDashboard";
import { isGymWorkspace } from "../gymLiveDashboard";
import CollegeLiveDashboard from "./CollegeLiveDashboard";
import { isCollegeWorkspace } from "../collegeLiveDashboard";
import { SchoolAtmosphere } from "./SchoolAtmosphere";
import "../styles/schoolLiveDashboard.css";
import { buildDemoWorkspaceChallenges, buildDemoWorkspaceMembers, mergeDemoWorkspaceGroupTotals, isDemoWorkspaceSettings } from "../institutionLiveDashboardDemo";
import { schoolGroupPastel, schoolMemberProgressColor, schoolMemberYearLabel } from "../schoolGroupPalette";

const DEFAULT_SETTINGS = {
  primaryColor: "#0C69C8",
  accentColor: "#D5A419",
  supportColor: "",
  highlightColor: "",
  backgroundColor: "#070A12",
  sections: ["stats", "members", "teams"],
};
const MEDAL_RANK_COLORS = {
  light: ["#D4AF37", "#8F98A8", "#CD7F32"],
  dark: ["#D4AF37", "#8F98A8", "#CD7F32"],
};

const fmt = (value) => Number(value || 0).toLocaleString();
const stepText = (locked, value) => locked ? "Locked" : fmt(value);
const plural = (count, singular, pluralLabel = `${singular}s`) => `${fmt(count)} ${Number(count || 0) === 1 ? singular : pluralLabel}`;
const initials = (name = "Member") => name.split(" ").filter(Boolean).slice(0, 2).map(part => part[0]).join("").toUpperCase() || "M";
const stepProgress = (steps, maxSteps) => {
  const current = Number(steps || 0);
  const max = Number(maxSteps || 0);
  return current > 0 && max > 0 ? Math.round((current / max) * 100) : 0;
};
const cleanHex = (value, fallback) => /^#[0-9a-f]{6}$/i.test(String(value || "").trim()) ? String(value).trim() : fallback;
const hexToRgb = (hex = "") => {
  const match = String(hex).trim().match(/^#([0-9a-f]{6})$/i);
  if (!match) return { r: 7, g: 10, b: 18 };
  const value = parseInt(match[1], 16);
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
};
const rgba = (hex, alpha) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
};
const mix = (hex, target, amount) => {
  const from = hexToRgb(hex);
  const to = hexToRgb(target);
  const next = {
    r: Math.round(from.r + (to.r - from.r) * amount),
    g: Math.round(from.g + (to.g - from.g) * amount),
    b: Math.round(from.b + (to.b - from.b) * amount),
  };
  return `#${[next.r, next.g, next.b].map(v => v.toString(16).padStart(2, "0")).join("")}`;
};
const tint = (hex, percentage) => mix(hex, "#FFFFFF", Math.max(0, Math.min(1, percentage / 100)));
const shade = (hex, percentage) => mix(hex, "#000000", Math.max(0, Math.min(1, percentage / 100)));
const isLightColor = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  return ((0.2126 * r) + (0.7152 * g) + (0.0722 * b)) > 168;
};
const relativeLuminance = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  const normalize = (value) => {
    const next = value / 255;
    return next <= 0.03928 ? next / 12.92 : Math.pow((next + 0.055) / 1.055, 2.4);
  };
  return (0.2126 * normalize(r)) + (0.7152 * normalize(g)) + (0.0722 * normalize(b));
};
const contrastRatio = (a, b) => {
  const light = Math.max(relativeLuminance(a), relativeLuminance(b));
  const dark = Math.min(relativeLuminance(a), relativeLuminance(b));
  return (light + 0.05) / (dark + 0.05);
};
const getReadableTextColor = (hex) => contrastRatio(hex, "#FFFFFF") >= 4.5 ? "#FFFFFF" : "#202124";
const safePageBackground = (background, primary, accent) => {
  if (!isLightColor(background)) return `radial-gradient(circle at top left, ${rgba(primary, 0.20)}, transparent 32%), ${shade(background, 8)}`;
  return `linear-gradient(180deg, ${tint(primary, 94)} 0%, ${background} 34%, ${tint(accent, 96)} 100%)`;
};
const mapBrandColorsToRoles = (colors = {}) => {
  const primary = cleanHex(colors.primaryColor, DEFAULT_SETTINGS.primaryColor);
  const accent = cleanHex(colors.accentColor, DEFAULT_SETTINGS.accentColor);
  const support = cleanHex(colors.supportColor, primary);
  const highlight = cleanHex(colors.highlightColor, accent);
  const background = cleanHex(colors.backgroundColor, DEFAULT_SETTINGS.backgroundColor);
  return { primary, accent, support, highlight, background };
};
const normalizeGroupLabel = (value) => {
  const label = String(value || "").trim().toLowerCase();
  if (label === "academic groups") return "Academic Groups";
  return label === "departments" ? "Departments" : "Teams";
};
const generateBrandTheme = (colors = {}) => {
  const roles = mapBrandColorsToRoles(colors);
  const light = isLightColor(roles.background);
  const page = safePageBackground(roles.background, roles.primary, roles.accent);
  const text = getReadableTextColor(roles.background);
  const textOnPrimary = getReadableTextColor(roles.primary);
  const textOnAccent = getReadableTextColor(roles.accent);
  const textOnSupport = getReadableTextColor(roles.support);
  const textOnHighlight = getReadableTextColor(roles.highlight);
  const cardBackground = light ? rgba("#FFFFFF", 0.92) : rgba("#FFFFFF", 0.07);
  const elevatedCardBackground = light ? rgba(tint(roles.primary, 96), 0.96) : rgba("#000000", 0.24);
  const medalRankColors = light ? MEDAL_RANK_COLORS.light : MEDAL_RANK_COLORS.dark;
  const borderColor = rgba(roles.primary, light ? 0.20 : 0.30);
  const subtleBorderColor = rgba(roles.primary, light ? 0.13 : 0.18);
  const textSecondary = light ? "rgba(32,33,36,0.80)" : "rgba(255,255,255,0.80)";
  const mutedText = light ? "rgba(32,33,36,0.66)" : "rgba(255,255,255,0.68)";
  const progressTrack = light ? rgba(roles.primary, 0.11) : rgba("#FFFFFF", 0.12);
  return {
    ...roles,
    light,
    page,
    pageBackground: page,
    cardBackground,
    elevatedCardBackground,
    primaryTint: rgba(roles.primary, light ? 0.12 : 0.18),
    accentTint: rgba(roles.accent, light ? 0.13 : 0.18),
    supportTint: rgba(roles.support, light ? 0.12 : 0.18),
    highlightTint: rgba(roles.highlight, light ? 0.16 : 0.20),
    borderColor,
    subtleBorderColor,
    textPrimary: text,
    textSecondary,
    mutedText,
    progressTrack,
    hoverSurface: light ? rgba(roles.primary, 0.075) : rgba("#FFFFFF", 0.075),
    onPrimary: textOnPrimary,
    onAccent: textOnAccent,
    onSupport: textOnSupport,
    onHighlight: textOnHighlight,
    contrastRatio,
    text,
    textOnPrimary,
    textOnAccent,
    textOnSupport,
    textOnHighlight,
    textMuted: mutedText,
    textSoft: textSecondary,
    surface: cardBackground,
    surfaceStrong: elevatedCardBackground,
    surfaceSubtle: light ? rgba(roles.primary, 0.075) : rgba("#FFFFFF", 0.075),
    border: borderColor,
    borderSoft: subtleBorderColor,
    borderAccent: rgba(roles.accent, 0.42),
    shadow: `0 22px 60px ${rgba(shade(roles.primary, 35), light ? 0.14 : 0.32)}`,
    shadowSoft: `0 12px 34px ${rgba(shade(roles.primary, 40), light ? 0.10 : 0.22)}`,
    track: progressTrack,
    header: light ? rgba(tint(roles.primary, 96), 0.92) : rgba(shade(roles.background, 30), 0.86),
    hero: `radial-gradient(circle at 16% 16%, ${rgba(roles.highlight, light ? 0.38 : 0.26)}, transparent 24%),
      radial-gradient(circle at 88% 10%, ${rgba(roles.accent, light ? 0.28 : 0.18)}, transparent 30%),
      linear-gradient(135deg, ${rgba(roles.primary, light ? 0.24 : 0.48)}, ${rgba(roles.support, light ? 0.13 : 0.26)} 48%, ${rgba(roles.accent, light ? 0.14 : 0.20)}),
      ${light ? rgba("#FFFFFF", 0.88) : rgba("#FFFFFF", 0.07)}`,
    heroPattern: `linear-gradient(90deg, ${rgba(roles.primary, 0.18)} 1px, transparent 1px), linear-gradient(${rgba(roles.primary, 0.12)} 1px, transparent 1px)`,
    chipBg: rgba(roles.accent, light ? 0.14 : 0.18),
    chipBorder: rgba(roles.accent, 0.46),
    chipText: light ? shade(roles.accent, 34) : tint(roles.accent, 20),
    rank: [...medalRankColors, light ? roles.primary : "#FFFFFF"],
    progressFill: roles.accent,
  };
};

const PersonImage = ({ member, size = 54, theme }) => (
  member?.image ? (
    <img src={member.image} alt="" style={{ width: size, height: size, borderRadius: 999, objectFit: "cover", border: `2px solid ${rgba(theme.primary, 0.18)}`, flexShrink: 0 }} loading="lazy" />
  ) : (
    <div aria-hidden="true" style={{ width: size, height: size, borderRadius: 999, display: "grid", placeItems: "center", background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`, color: theme.textOnPrimary, border: `2px solid ${rgba(theme.highlight, 0.28)}`, fontWeight: 950, flexShrink: 0 }}>
      {initials(member?.name)}
    </div>
  )
);

const Stat = ({ icon, label, value, color, theme }) => (
  <div className="live-metric">
    <div aria-hidden="true" className="live-metric-icon" style={{ background: rgba(color, theme.light ? 0.10 : 0.18), color }}>
      <FontAwesomeIcon icon={icon} />
    </div>
    <div>
      <div style={{ color: theme.textMuted }} className="live-metric-label">{label}</div>
      <div style={{ color: theme.text }} className="live-metric-value">{value}</div>
    </div>
  </div>
);

const SectionHead = ({ eyebrow, title, sub, theme }) => (
  <div className="live-section-heading">
    <div style={{ color: theme.primary }} className="live-section-eyebrow">{eyebrow}</div>
    <h2 style={{ color: theme.text }}>{title}</h2>
    {sub && <p style={{ color: theme.textMuted }}>{sub}</p>}
  </div>
);

const SchoolGroupBadge = ({ name, className }) => {
  const pastel = schoolGroupPastel(name);
  return <span className={className} style={{ color: pastel.ink, background: pastel.surface, borderColor: pastel.border }}>{schoolMemberYearLabel(name)}</span>;
};

const withDemoMembers = async (dashboard) => {
  const workspace = dashboard?.workspace || {};
  if (!workspace?.id || !isDemoWorkspaceSettings(workspace?.settings)) return dashboard;

  const { data: institutes, error: instituteError } = await supabase
    .from("institutes")
    .select("id,name,institute_type,teams,image_url,brand_logo_url")
    .eq("workspace_id", workspace.id)
    .is("archived_at", null);
  if (instituteError) throw instituteError;

  const memberInstitute = (institutes || []).find((row) => (
    row.teams === false && row.institute_type !== "Challenge" && /\bmembers\b/i.test(row.name || "")
  )) || (institutes || []).find((row) => row.teams === false && row.institute_type !== "Challenge");
  if (!memberInstitute?.id) return dashboard;

  const challengeInstitutes = (institutes || []).filter((row) => String(row.institute_type || "").toLowerCase() === "challenge");
  const includedInstituteIds = [memberInstitute.id, ...challengeInstitutes.map((row) => row.id), ...(institutes || []).filter((row) => row.teams === true).map((row) => row.id)];
  const { data: memberships, error: membershipError } = await supabase
    .from("demo_user_institutes")
    .select("demo_profile_id,institute_id,team_id,steps")
    .in("institute_id", includedInstituteIds)
    .eq("status", "Active");
  if (membershipError) throw membershipError;
  if (!memberships?.length) return dashboard;

  const memberMemberships = memberships.filter((row) => row.institute_id === memberInstitute.id);
  const profileIds = [...new Set(memberMemberships.map((row) => row.demo_profile_id).filter(Boolean))];
  const { data: profiles, error: profileError } = await supabase
    .from("demo_profiles")
    .select("id,name,surname,username,profile_img")
    .in("id", profileIds);
  if (profileError) throw profileError;

  const schoolGroupById = new Map((dashboard?.members || []).map((member) => [String(member.id), member.school_group]));
  const demoMembers = buildDemoWorkspaceMembers(memberMemberships, profiles).map((member) => ({
    ...member,
    school_group: schoolGroupById.get(String(member.id)) || null,
  }));
  const demoChallenges = buildDemoWorkspaceChallenges(challengeInstitutes, memberships);
  const challengeIds = new Set(demoChallenges.map((challenge) => String(challenge.id)));

  return {
    ...dashboard,
    // Demo workspaces must present only their own fictional roster consistently.
    // Do not mix the real demo-account owner/admin profiles into Top Movers.
    members: demoMembers,
    teams: [
      ...mergeDemoWorkspaceGroupTotals((dashboard?.teams || []).filter((team) => !challengeIds.has(String(team.id))), memberships, profileIds),
      ...demoChallenges,
    ],
  };
};

export default function InstitutionLiveDashboard() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const workspaceId = params.get("workspace_id");
    const instituteId = params.get("institute_id");
    if (!workspaceId && !instituteId) return;
    params.delete("workspace_id");
    params.delete("institute_id");
    const existingContext = location.state?.institutionReturn || {};
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : "" }, {
      replace: true,
      state: {
        ...(location.state || {}),
        institutionReturn: {
          workspaceId: workspaceId || existingContext.workspaceId || "",
          instituteId: instituteId || existingContext.instituteId || "",
        },
      },
    });
  }, [location.pathname, location.search, location.state, navigate]);
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loadedSlug, setLoadedSlug] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let active = true;
    setShowAllMembers(false);
    setLoading(true);
    setError("");
    supabase.rpc("get_institution_live_dashboard", { p_slug: slug })
      .then(async ({ data, error: rpcError }) => {
        if (!active) return;
        if (rpcError) throw rpcError;
        const nextDashboard = await withDemoMembers(data);
        if (active) {
          setDashboard(nextDashboard);
          setLoadedSlug(slug);
        }
      })
      .catch((rpcError) => active && setError(rpcError?.message || "Could not load live dashboard."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [slug, user?.id, retryCount]);

  useEffect(() => {
    if (loadedSlug !== slug || !dashboard?.workspace?.name) return;
    const canonicalSlug = workspaceNameSlug(dashboard.workspace.name);
    if (slug === canonicalSlug) return;
    const routePrefix = location.pathname.slice(0, location.pathname.lastIndexOf("/") + 1);
    navigate(`${routePrefix}${canonicalSlug}${location.search}`, {
      replace: true,
      state: location.state,
    });
  }, [dashboard?.workspace?.name, loadedSlug, slug, location.pathname, location.search, location.state, navigate]);

  const autoRefreshDashboard = dashboard?.workspace?.is_school || isGymWorkspace(dashboard?.workspace) || isCollegeWorkspace(dashboard?.workspace);
  // Keep visible institution dashboards current without clearing the standings.
  useEffect(() => {
    if (!autoRefreshDashboard) return undefined;
    let active = true;
    let pending = false;
    const refresh = async () => {
      if (document.hidden || pending) return;
      pending = true;
      try {
        const { data, error: rpcError } = await supabase.rpc("get_institution_live_dashboard", { p_slug: slug });
        if (rpcError) throw rpcError;
        const next = await withDemoMembers(data);
        if (active) { setDashboard(next); setRefreshError(false); }
      } catch (rpcError) {
        if (active) {
          if (/Live dashboard not found|Sign in with|available only/.test(rpcError?.message || '')) { setError(rpcError.message); setDashboard(null); }
          else setRefreshError(true);
        }
      }
      finally { pending = false; }
    };
    const interval = setInterval(refresh, 30000);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('online', refresh);
    return () => { active = false; clearInterval(interval); document.removeEventListener('visibilitychange', refresh); window.removeEventListener('online', refresh); };
  }, [autoRefreshDashboard, slug]);

  const workspace = dashboard?.workspace || {};
  const settings = { ...DEFAULT_SETTINGS, ...(workspace.settings || {}) };
  const workspaceLogoUrl = workspace.logo_url || "";
  const groupLabel = normalizeGroupLabel(workspace.settings?.groupLabel || workspace.group_label);
  const groupLabelLower = groupLabel.toLowerCase();
  const stepsLocked = workspace.steps_locked === true || workspace.access_active === false;
  const members = useMemo(() => {
    const rows = dashboard?.members || [];
    return stepsLocked ? [...rows].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" })) : rows;
  }, [dashboard?.members, stepsLocked]);
  const teams = useMemo(() => {
    const rows = dashboard?.teams || [];
    return stepsLocked ? [...rows].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" })) : rows;
  }, [dashboard?.teams, stepsLocked]);
  const departmentTeams = useMemo(() => teams.filter(team => team.kind !== "challenge"), [teams]);
  const challengeTeams = useMemo(() => teams.filter(team => team.kind === "challenge"), [teams]);
  const listedTotalSteps = useMemo(() => members.reduce((sum, member) => sum + Number(member.steps || 0), 0), [members]);
  const collegeDemoRoster = isDemoWorkspaceSettings(workspace?.settings) && String(workspace?.settings?.category || workspace?.settings?.segment || "").trim().toLowerCase() === "college";
  const memberCount = collegeDemoRoster || workspace.activity_summary?.member_count == null ? members.length : Number(workspace.activity_summary.member_count);
  const totalSteps = collegeDemoRoster || workspace.activity_summary?.total_steps == null ? listedTotalSteps : Number(workspace.activity_summary.total_steps);
  const averageSteps = memberCount ? Math.round(totalSteps / memberCount) : 0;
  const topMember = stepsLocked ? null : members.find(member => Number(member.steps || 0) > 0) || null;
  const featuredMembers = members.slice(0, 12);
  const maxMemberSteps = stepsLocked ? 0 : Math.max(0, ...members.map(member => Number(member.steps || 0)));
  const maxDepartmentSteps = stepsLocked ? 0 : Math.max(0, ...departmentTeams.map(team => Number(team.steps || 0)));
  const maxChallengeSteps = stepsLocked ? 0 : Math.max(0, ...challengeTeams.map(team => Number(team.steps || 0)));
  const isSchool = workspace.is_school === true;
  const isSchoolLink = location.pathname.startsWith('/institutions/schools/');
  const wrongSchoolAccount = isSchoolLink && /Sign in with a workspace member account|available only to the demo account/i.test(error);
  const theme = generateBrandTheme(isSchool ? { primaryColor: '#0866ce', accentColor: '#956916', supportColor: '#267c68', highlightColor: '#8054b3', backgroundColor: '#ffffff' } : settings);
  const schoolBrand = cleanHex(settings.primaryColor, '#0866ce');
  const statContrastColor = theme.light ? theme.primary : "#FFFFFF";
  const isCollege = String(workspace?.settings?.category || workspace?.settings?.segment || "").trim().toLowerCase() === "college";
  const isAfriRunDemo = String(workspace?.settings?.demo_seed || "") === "afrirun-institute-demo-v1";
  const isJustUsDemo = String(workspace?.settings?.demo_seed || "") === "just-us-institute-demo-v1";
  const peopleLabel = isCollege ? "Students" : "Members";
  const peopleLabelLower = peopleLabel.toLowerCase();
  const internalGroupLabel = isSchool ? "Year groups" : isCollege ? "Academic Groups" : isAfriRunDemo ? "Run crews" : isJustUsDemo ? "Circles" : groupLabel;
  const campusImageUrl = departmentTeams.find(team => team?.image)?.image || challengeTeams.find(team => team?.image)?.image || "";
  const activeChallengeCount = challengeTeams.length;
  const cleanChallengeName = (name = "") => String(name).replace(new RegExp(`^${String(workspace.name || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*`, "i"), "").trim() || name;
  const returnWorkspaceId = location.state?.institutionReturn?.workspaceId || workspace.id || "";
  const returnInstituteId = location.state?.institutionReturn?.instituteId || "";
  const workspaceReturnPath = "/profile?tab=institutes";
  const workspaceReturnState = { institutionReturn: { workspaceId: returnWorkspaceId, instituteId: returnInstituteId } };
  const canReturnToWorkspace = workspace.can_return === true;

  if (!loading && !error && isGymWorkspace(workspace)) {
    return <GymLiveDashboard dashboard={dashboard} returnPath={workspaceReturnPath} returnState={workspaceReturnState} refreshError={refreshError} />;
  }

  if (!loading && !error && isCollegeWorkspace(workspace)) {
    return <CollegeLiveDashboard dashboard={dashboard} returnPath={workspaceReturnPath} returnState={workspaceReturnState} refreshError={refreshError} />;
  }

  return (
    <MotionConfig reducedMotion="user"><main className={`live-dashboard-page ${isSchool ? "live-school-dashboard" : ""} ${isCollege ? "is-college" : ""}`} style={{ "--school-brand": schoolBrand, "--school-tint": tint(schoolBrand, 94), minHeight: "100vh", background: theme.page, color: theme.text, fontFamily: "'DM Sans',sans-serif", overflowX: "clip" }}>
      {canReturnToWorkspace && <header className="live-dashboard-topbar" style={{ borderBottom: `1px solid ${theme.border}`, background: theme.header }}>
        <div className="live-dashboard-topbar-inner">
          <Link to={workspaceReturnPath} state={workspaceReturnState} className="live-back-link" style={{ color: theme.primary, background: rgba(theme.primary, 0.07), border: `1px solid ${rgba(theme.primary, 0.14)}` }}>
            <FontAwesomeIcon aria-hidden="true" icon={faArrowLeft} /> Back to workspace
          </Link>
        </div>
      </header>}

      {loading ? (
        <div role="status" style={{ minHeight: "calc(100vh - 66px)", display: "grid", placeItems: "center", color: theme.primary, fontWeight: 900 }}>Loading live workspace...</div>
      ) : error ? (
        <div role="alert" style={{ minHeight: "calc(100vh - 66px)", display: "grid", placeItems: "center", padding: 20 }}>
          <section style={{ maxWidth: 560, borderRadius: 20, padding: 22, background: theme.surface, border: `1px solid ${theme.borderAccent}`, boxShadow: theme.shadow }}>
            <FontAwesomeIcon aria-hidden="true" icon={faLock} style={{ color: theme.primary, marginBottom: 12 }} />
            <h1 style={{ margin: "0 0 8px", fontSize: 26 }}>{wrongSchoolAccount ? 'Sign in with your school account' : isSchoolLink ? 'School dashboard could not load' : 'Live dashboard unavailable'}</h1>
            <p style={{ color: theme.textMuted, lineHeight: 1.6, margin: "0 0 14px" }}>
              {wrongSchoolAccount
                ? 'This account is not linked to this school. Use the Snows ProAm account you joined the school with, then open this link again.'
                : isSchoolLink
                  ? 'We could not load your school dashboard right now. Please try again. If it still does not open, contact Eva for help.'
                  : 'This live page is not available. Please try again or contact Snows ProAm for help.'}
            </p>
            <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
              {wrongSchoolAccount ? (
                <button type="button" onClick={() => supabase.auth.signOut()} style={{ minHeight: 42, padding: '9px 13px', borderRadius: 10, border: 0, background: theme.primary, color: theme.textOnPrimary, fontWeight: 900, cursor: 'pointer' }}>Switch account</button>
              ) : (
                <button type="button" onClick={() => setRetryCount((count) => count + 1)} style={{ minHeight: 42, padding: '9px 13px', borderRadius: 10, background: theme.primary, color: theme.textOnPrimary, border: 0, fontWeight: 900, cursor: 'pointer' }}>Try again</button>
              )}
              {isSchoolLink && <a href="mailto:eva.bellova@snowsproam.com" style={{ color: theme.primary, fontWeight: 900 }}>Contact Eva</a>}
            </div>
          </section>
        </div>
      ) : (
        <section className="live-workspace-shell">
          {isSchool && <div className="school-page-heading"><div><span className="school-live-tag"><FontAwesomeIcon icon={faShoePrints} aria-hidden="true" /> YOUR SCHOOL IN MOTION</span><p>Big steps. Bigger possibilities.</p></div><a href="#school-movers" className="school-leaderboard-link">Find your place ↓</a></div>}
          <section className="live-workspace-hero" style={{ border: `1px solid ${theme.borderAccent}`, boxShadow: theme.shadow }}>
            <div className="live-hero-copy" style={{ background: isSchool ? `linear-gradient(125deg, #f0f8ff, #f5f0ff 65%, ${tint(schoolBrand, 94)})` : `linear-gradient(145deg, ${shade(theme.primary, 18)} 0%, ${theme.primary} 58%, ${shade(theme.support, 12)} 130%)`, color: isSchool ? theme.text : theme.textOnPrimary }}>
              <div aria-hidden="true" style={{ position: "absolute", inset: 0, backgroundImage: theme.heroPattern, backgroundSize: "38px 38px", opacity: 0.12 }} />
              <div style={{ position: "relative", zIndex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 18 }}>
                {workspaceLogoUrl ? (
                  <img src={workspaceLogoUrl} alt={`${workspace.name} logo`} className="live-hero-logo" />
                ) : (
                  <div aria-hidden="true" className="live-hero-logo live-hero-logo-fallback" style={{ color: theme.textOnPrimary }}>
                    <FontAwesomeIcon icon={faBuilding} size="2x" />
                  </div>
                )}
                <div style={{ minWidth: 0 }}>
                  <div className="live-hero-kicker" style={{ color: isSchool ? "#486681" : rgba("#FFFFFF", 0.72) }}>{isSchool ? "School spirit · Every step counts" : isCollege ? "Life across campus" : isAfriRunDemo ? "Belfast running community" : isJustUsDemo ? "Fitness community" : "Live activity"}</div>
                </div>
              </div>
              <h1 className="live-hero-title">{workspace.settings?.displayName || workspace.name || "Workspace"}</h1>
              <p className="live-hero-subtitle" style={{ color: isSchool ? "#516780" : rgba("#FFFFFF", 0.82) }}>{isSchool ? "Your people. Your year group. Your next personal best. See your school moving together." : isCollege ? "One campus, many disciplines. Explore how students and Academic Groups are moving together." : isAfriRunDemo ? "One community, many paces. See how AfriRun members and run crews are moving together across Belfast." : `Explore the people, activity, ${groupLabelLower}, and challenges shaping this workspace.`}</p>
              {stepsLocked && (
                <div role="status" className="live-locked-status" style={{ background: rgba(theme.highlight, 0.14), border: `1px solid ${rgba(theme.highlight, 0.34)}`, color: theme.highlight }}>
                  <FontAwesomeIcon icon={faLock} aria-hidden="true" />
                  Step values are locked until institution access is active again.
                </div>
              )}
              <div className="live-hero-facts" aria-label="Workspace activity summary">
                <span>{plural(memberCount, isCollege ? "student" : "member")} active</span>
                <span>{plural(departmentTeams.length, isSchool ? "year group" : isCollege ? "academic group" : isAfriRunDemo ? "run crew" : groupLabel.slice(0, -1) || "group")}</span>
                <span>{plural(activeChallengeCount, "active challenge")}</span>
              </div>
              {featuredMembers.length > 0 && (
                <div aria-label={`Featured ${peopleLabelLower}`} className="live-featured-people">
                  <div className="live-featured-avatars">
                    {featuredMembers.slice(0, 6).map((member, index) => (
                      <div key={member.id} style={{ marginLeft: index ? -10 : 0 }}>
                        <PersonImage member={member} size={42} theme={theme} />
                      </div>
                    ))}
                  </div>
                  <span>Moving together today</span>
                </div>
              )}
              </div>
            </div>

            <aside className="live-hero-visual" style={{ background: `linear-gradient(150deg, ${rgba(theme.primary, 0.16)}, ${rgba(theme.support, 0.18)})` }}>
              {campusImageUrl ? <img src={campusImageUrl} alt={isCollege ? `${workspace.name || "University"} campus` : `${workspace.name || "Institution"} ${isSchool ? "campus" : isAfriRunDemo ? "running community" : "workspace"}`} className="live-campus-photo" /> : <div className="live-campus-placeholder"><FontAwesomeIcon icon={faBuilding} size="3x" /></div>}
              <div aria-hidden="true" className="live-campus-shade" />
              <div className="live-leader-card">
                <div className="live-leader-label"><FontAwesomeIcon icon={faMedal} aria-hidden="true" /> {stepsLocked ? "Step values locked" : isCollege ? "Current student leader" : "Current member leader"}</div>
                {topMember ? <div className="live-leader-person"><PersonImage member={topMember} size={58} theme={theme} /><div><strong>{topMember.name}</strong>{isSchool && topMember.school_group && <SchoolGroupBadge name={topMember.school_group} className="live-leader-school-group" />}<span>{stepText(stepsLocked, topMember.steps)}{stepsLocked ? "" : " steps"}</span></div></div> : <p>{stepsLocked ? "Leaderboard values are temporarily unavailable." : "A leader will appear after activity is synced."}</p>}
              </div>
            </aside>
          </section>

          {isSchool && <SchoolAtmosphere />}

          <section className="live-stats-strip" style={{ background: theme.surface, border: `1px solid ${theme.border}` }} aria-label="Workspace statistics">
            <Stat theme={theme} icon={faShoePrints} label="Total steps" value={stepText(stepsLocked, totalSteps)} color={theme.accent} />
            <Stat theme={theme} icon={faUsers} label={memberCount === 1 ? peopleLabel.slice(0, -1) : peopleLabel} value={fmt(memberCount)} color={statContrastColor} />
            <Stat theme={theme} icon={faLayerGroup} label={internalGroupLabel} value={fmt(departmentTeams.length)} color={theme.accent} />
            <Stat theme={theme} icon={faChartLine} label="Average steps" value={stepText(stepsLocked, averageSteps)} color={statContrastColor} />
          </section>

          {isSchool && refreshError && <p role="status" className="school-refresh-notice">Updates are temporarily unavailable. Showing the last loaded standings; we’ll retry automatically.</p>}
          <section className="live-workspace-main-grid">
            <section id={isSchool ? "school-movers" : undefined} className="live-content-panel live-people-panel" style={{ background: theme.surface, border: `1px solid ${theme.border}` }}>
              <SectionHead theme={theme} eyebrow={`${peopleLabel} · live ranking`} title={isSchool ? "School movers" : isCollege ? "Campus movers" : isAfriRunDemo ? "AfriRun movers" : members.length === 1 ? "Top mover" : "Top movers"} sub={isSchool ? `A friendly snapshot of activity across ${workspace.name || "the school"}.` : isCollege ? "A snapshot of student activity from across the academic community." : isAfriRunDemo ? "A friendly snapshot of member activity across the AfriRun community." : `${peopleLabel} ranked by their current workspace step totals.`} />
              {members.length ? (
                <>
                <ol className="live-member-list" aria-label={`${peopleLabel} step leaderboard`}>
                  {members.slice(0, showAllMembers ? members.length : 10).map((member, index) => {
                    const steps = Number(member.steps || 0);
                    const pct = stepsLocked ? 0 : stepProgress(steps, maxMemberSteps);
                    const rankColor = theme.rank[Math.min(index, theme.rank.length - 1)];
                    const barColor = isSchool ? schoolMemberProgressColor(member.school_group, index) : index < 3 ? rankColor : theme.progressFill;
                    return (
                      <motion.li layout={isSchool ? "position" : false} transition={{ layout: { duration: 0.35 } }} key={member.id} className={index < 3 ? "is-leading" : ""} style={{ borderColor: theme.borderSoft }}>
                        <div className="live-rank-number" style={{ color: rankColor }}>{stepsLocked ? "—" : index + 1}</div>
                        <PersonImage member={member} size={index < 3 ? 52 : 44} theme={theme} />
                        <div className="live-member-info">
                          <div className="live-member-identity">
                            <div className="live-member-name" style={{ color: theme.text }}>{member.name}</div>
                            {isSchool && member.school_group && <SchoolGroupBadge name={member.school_group} className="live-member-school-group" />}
                          </div>
                          <div className="live-member-track" style={{ background: theme.track }} aria-hidden="true">
                            <div style={{ width: `${pct}%`, background: barColor }} />
                          </div>
                        </div>
                        <div className="live-member-steps" style={{ color: theme.text }}>{stepText(stepsLocked, member.steps)}{!stepsLocked && <span> steps</span>}</div>
                      </motion.li>
                    );
                  })}
                </ol>
                {members.length > 10 && (
                  <button type="button" className="live-show-more" style={{ color: theme.primary, borderColor: theme.border }} onClick={() => setShowAllMembers(value => !value)} aria-expanded={showAllMembers}>
                    {showAllMembers ? "Show top 10" : `${memberCount > members.length ? "Show top" : "Show all"} ${members.length} ${peopleLabelLower}`}
                  </button>
                )}
                </>
              ) : (
                <div style={{ margin: 0, color: theme.textMuted, borderRadius: 18, padding: 18, background: `linear-gradient(135deg, ${rgba(theme.primary, 0.08)}, ${rgba(theme.accent, 0.06)}), ${theme.surfaceStrong}`, border: `1px dashed ${theme.borderAccent}` }}>
                  <div style={{ color: theme.primary, fontWeight: 950, marginBottom: 6 }}>No steps yet</div>
                  Member activity will appear here once people join and sync steps.
                  <div aria-hidden="true" style={{ height: 8, borderRadius: 999, background: theme.track, overflow: "hidden", marginTop: 14 }}>
                    <div style={{ width: "28%", height: "100%", background: rgba(theme.primary, 0.34), borderRadius: 999 }} />
                  </div>
                </div>
              )}
            </section>

            <section className="live-side-stack">
              <section className="live-content-panel" style={{ background: theme.surface, border: `1px solid ${theme.border}` }}>
                <SectionHead theme={theme} eyebrow={isSchool ? "Across the school" : isCollege ? "Across the university" : isAfriRunDemo ? "Across the club" : groupLabel} title={internalGroupLabel} sub={isSchool ? "How each year group is contributing to the school total." : isCollege ? "See how each discipline contributes to the campus total." : isAfriRunDemo ? "How every run crew is contributing to AfriRun's total." : `Compare internal ${groupLabelLower} inside the workspace.`} />
                {departmentTeams.length ? (
                  <ol className="live-group-list" aria-label={`${internalGroupLabel} step ranking`}>
                    {departmentTeams.slice(0, 12).map((team, index) => {
                      const steps = Number(team.steps || 0);
                      const pct = stepsLocked ? 0 : stepProgress(steps, maxDepartmentSteps);
                      const schoolPastel = isSchool ? schoolGroupPastel(team.name) : null;
                      const justUsRankColors = [shade(theme.primary, 10), shade(theme.primary, 24), shade(theme.primary, 38), theme.primary];
                      const rowColor = schoolPastel ? schoolPastel.ink : isJustUsDemo
                        ? justUsRankColors[Math.min(index, justUsRankColors.length - 1)]
                        : index === 0 ? theme.highlight : index === 1 ? theme.support : index === 2 ? theme.accent : theme.primary;
                      return (
                        <li key={team.id} style={{ borderColor: schoolPastel?.border || theme.borderSoft, background: schoolPastel?.surface }}>
                          <div className={`live-group-row${isSchool ? " has-school-crest" : ""}`}>
                            <span className="live-group-rank" style={{ color: rowColor }}>{stepsLocked ? "—" : index + 1}</span>
                            {isSchool && <span className="live-school-crest" style={{ color: rowColor }} aria-hidden="true"><svg viewBox="0 0 44 48" focusable="false"><path d="M22 2 41 8v16c0 11-7 17.5-19 22C10 41.5 3 35 3 24V8L22 2Z" fill="white" stroke="currentColor" strokeWidth="1.7" /><path d="M22 5 38 10v14c0 9-5.5 15-16 19C11.5 39 6 33 6 24V10L22 5Z" fill="currentColor" opacity=".09" /><path d="M22 12v3m-3-1.5h6M12 20c3.6-1.2 7.1-.7 10 1.3 2.9-2 6.4-2.5 10-1.3v12c-3.6-1.2-7.1-.7-10 1.3-2.9-2-6.4-2.5-10-1.3V20Zm10 1.3v12" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg></span>}
                            <div className="live-group-name">
                              <strong style={{ color: theme.text }}>{team.name}</strong>
                              {isSchool && team.member_count != null && <span style={{ color: theme.textMuted }}>{plural(team.member_count, /^staff room$/i.test(team.name) ? "staff member" : "student")}</span>}
                            </div>
                            <span style={{ color: theme.text }}>{stepText(stepsLocked, team.steps)}{stepsLocked ? "" : " steps"}</span>
                          </div>
                          <div className="live-group-track" style={{ background: schoolPastel?.track || theme.track }} aria-hidden="true">
                            <div style={{ width: `${pct}%`, background: rowColor }} />
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                ) : (
                  <div style={{ margin: 0, color: theme.textMuted, borderRadius: 18, padding: 16, background: `linear-gradient(135deg, ${rgba(theme.support, 0.08)}, ${rgba(theme.primary, 0.06)}), ${theme.surfaceStrong}`, border: `1px dashed ${rgba(theme.support, 0.34)}` }}>No {groupLabelLower} have been created yet.</div>
                )}
              </section>
              <section className="live-content-panel" style={{ background: theme.surface, border: `1px solid ${theme.border}` }}>
                <SectionHead theme={theme} eyebrow={isSchool ? "School challenge" : isCollege ? "Campus challenge" : isAfriRunDemo ? "Club challenge" : isJustUsDemo ? "Community challenge" : "Challenge"} title="Active challenges" sub={isSchool ? "A shared goal that turns everyday movement into school-wide momentum." : isCollege ? "A shared campus goal connects students across every academic discipline." : isAfriRunDemo ? "A shared target that turns every run and walk into community momentum." : isJustUsDemo ? "A shared goal for the Just Us community to move, chat and keep one another motivated." : `Challenges are shown separately from ${groupLabelLower} so the live page stays easy to scan.`} />
                {challengeTeams.length > 0 ? (
                  <div className="live-challenge-list">
                    {challengeTeams.slice(0, 12).map((team, index) => {
                      const steps = Number(team.steps || 0);
                      const pct = stepsLocked ? 0 : stepProgress(steps, maxChallengeSteps);
                      return (
                        <article key={team.id} className="live-challenge-card" style={{ background: `linear-gradient(135deg, ${rgba(theme.support, 0.15)}, ${rgba(theme.highlight, 0.10)})`, border: `1px solid ${rgba(theme.support, 0.26)}` }}>
                          <div className="live-challenge-icon" aria-hidden="true" style={{ background: theme.support, color: theme.textOnSupport }}><FontAwesomeIcon icon={faShoePrints} /></div>
                          <div className="live-challenge-copy">
                            <div className="live-challenge-label" style={{ color: theme.primary }}>{challengeTeams.length > 1 ? `Challenge ${index + 1}` : isSchool ? "School-wide challenge" : isCollege ? "Campus-wide challenge" : isAfriRunDemo ? "Club challenge" : isJustUsDemo ? "Community challenge" : "Workspace challenge"}</div>
                            <strong style={{ color: theme.text }}>{cleanChallengeName(team.name)}</strong>
                            <span style={{ color: theme.textMuted }}>{stepText(stepsLocked, team.steps)}{stepsLocked ? "" : " steps recorded"}</span>
                          </div>
                          <div className="live-challenge-track" style={{ background: theme.track }} aria-hidden="true">
                            <div style={{ width: `${pct}%`, background: theme.support }} />
                          </div>
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ borderRadius: 18, padding: 16, background: `linear-gradient(135deg, ${rgba(theme.highlight, 0.10)}, ${rgba(theme.primary, 0.06)}), ${theme.surfaceStrong}`, border: `1px dashed ${rgba(theme.highlight, 0.38)}` }}>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: theme.highlight, background: rgba(theme.highlight, 0.12), border: `1px solid ${rgba(theme.highlight, 0.30)}`, borderRadius: 999, padding: "5px 9px", fontSize: 11, fontWeight: 950, marginBottom: 8 }}>
                      <FontAwesomeIcon icon={faMedal} aria-hidden="true" />
                      No active challenges
                    </div>
                    <p style={{ margin: 0, color: theme.textMuted, lineHeight: 1.55 }}>Challenge leaderboards will appear here when a workspace challenge is active.</p>
                  </div>
                )}
              </section>
            </section>
          </section>
          {isSchool && <footer className="school-live-footer"><div><strong>Snows ProAm</strong><span>Where Athletes Belong</span></div><nav aria-label="Follow Snows ProAm"><a href="https://www.instagram.com/snowsproam/" target="_blank" rel="noopener noreferrer"><FontAwesomeIcon icon={faInstagram} aria-hidden="true"/>Instagram</a><a href="https://www.tiktok.com/@snowsproam" target="_blank" rel="noopener noreferrer"><FontAwesomeIcon icon={faTiktok} aria-hidden="true"/>TikTok</a></nav></footer>}
        </section>
      )}

      <style>{`
        .live-dashboard-page,
        .live-dashboard-page * { box-sizing: border-box; }
        .live-dashboard-topbar {
          min-height: 66px;
          position: sticky;
          top: 0;
          z-index: 5;
          backdrop-filter: blur(16px);
        }
        .live-dashboard-topbar-inner {
          width: min(1180px, 100%);
          min-height: 66px;
          margin: 0 auto;
          padding: 8px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
        }
        .live-back-link {
          min-height: 44px;
          padding: 0 14px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          gap: 9px;
          text-decoration: none;
          font-weight: 900;
        }
        .live-back-link:focus-visible,
        .live-show-more:focus-visible { outline: 3px solid ${rgba(theme.accent, 0.55)}; outline-offset: 3px; }
        .live-workspace-shell {
          width: min(1180px, 100%);
          margin: 0 auto;
          padding: 26px 20px 86px;
        }
        .live-workspace-hero {
          min-height: 430px;
          display: grid;
          grid-template-columns: minmax(0, 1.02fr) minmax(360px, .98fr);
          overflow: hidden;
          border-radius: 32px;
          margin-bottom: 18px;
        }
        .is-college .live-workspace-hero { grid-template-columns: minmax(0, .88fr) minmax(400px, 1.12fr); border-radius: 12px; }
        .is-college .live-hero-title { font-family: Georgia, "Times New Roman", serif; font-weight: 700; letter-spacing: -.035em; line-height: 1.04; }
        .is-college .live-hero-copy, .is-college .live-hero-visual { min-height: 480px; }
        .is-college .live-hero-kicker { letter-spacing: .18em; }
        .live-hero-copy {
          min-height: 430px;
          padding: clamp(28px, 4vw, 50px);
          position: relative;
          display: flex;
          align-items: center;
        }
        .live-hero-logo {
          width: 76px;
          height: 76px;
          padding: 7px;
          border: 1px solid rgba(255,255,255,.5);
          border-radius: 20px;
          object-fit: contain;
          background: rgba(255,255,255,.94);
          box-shadow: 0 16px 38px rgba(0,0,0,.16);
          flex: 0 0 auto;
        }
        .live-hero-logo-fallback { display: grid; place-items: center; background: rgba(255,255,255,.12); }
        .live-hero-kicker {
          font-size: 11px;
          font-weight: 950;
          letter-spacing: .14em;
          text-transform: uppercase;
        }
        .live-hero-title {
          max-width: 650px;
          margin: 0;
          color: #fff;
          font-size: clamp(44px, 6.5vw, 78px);
          font-weight: 950;
          line-height: .97;
          letter-spacing: -.045em;
        }
        .live-hero-subtitle {
          max-width: 620px;
          margin: 20px 0 0;
          font-size: clamp(16px, 1.8vw, 19px);
          line-height: 1.6;
        }
        .live-locked-status {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-top: 18px;
          padding: 9px 12px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 950;
        }
        .live-hero-facts {
          display: flex;
          flex-wrap: wrap;
          gap: 8px 16px;
          margin-top: 26px;
          color: rgba(255,255,255,.84);
          font-size: 13px;
          font-weight: 850;
        }
        .live-hero-facts span + span::before { content: "•"; margin-right: 16px; color: rgba(255,255,255,.42); }
        .live-featured-people {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 26px;
          color: rgba(255,255,255,.74);
          font-size: 12px;
          font-weight: 800;
        }
        .live-featured-avatars { display: flex; align-items: center; }
        .live-featured-avatars > div { border-radius: 999px; box-shadow: 0 0 0 2px rgba(255,255,255,.82); }
        .live-hero-visual { min-height: 430px; position: relative; overflow: hidden; }
        .live-campus-photo { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
        .live-campus-placeholder { position: absolute; inset: 0; display: grid; place-items: center; opacity: .35; }
        .live-campus-shade { position: absolute; inset: 0; background: linear-gradient(180deg, transparent 28%, rgba(8,12,18,.75) 100%); }
        .live-leader-card {
          position: absolute;
          left: 24px;
          right: 24px;
          bottom: 24px;
          padding: 18px;
          border: 1px solid rgba(255,255,255,.34);
          border-radius: 22px;
          background: rgba(16,18,24,.72);
          color: #fff;
          backdrop-filter: blur(15px);
          box-shadow: 0 18px 50px rgba(0,0,0,.22);
        }
        .live-leader-label { display: flex; align-items: center; gap: 8px; color: rgba(255,255,255,.72); font-size: 10px; font-weight: 950; letter-spacing: .12em; text-transform: uppercase; }
        .live-leader-person { display: flex; align-items: center; gap: 13px; margin-top: 12px; }
        .live-leader-person strong { display: block; font-size: 20px; line-height: 1.1; }
        .live-leader-person span { display: block; margin-top: 4px; color: rgba(255,255,255,.76); font-size: 14px; font-weight: 850; }
        .live-leader-person .live-leader-school-group { display: inline-flex; width: fit-content; margin-top: 5px; padding: 3px 8px; border: 1px solid; border-radius: 999px; font-size: 11px; font-weight: 750; }
        .live-leader-card p { margin: 12px 0 0; color: rgba(255,255,255,.76); }
        .live-stats-strip {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          margin-bottom: 24px;
          padding: 8px;
          border-radius: 22px;
        }
        .live-metric {
          min-width: 0;
          min-height: 92px;
          padding: 15px 18px;
          display: flex;
          align-items: center;
          gap: 13px;
          border-left: 1px solid ${theme.borderSoft};
        }
        .live-metric:first-child { border-left: 0; }
        .live-metric-icon { width: 38px; height: 38px; border-radius: 999px; display: grid; place-items: center; flex: 0 0 auto; }
        .live-metric-label { font-size: 10px; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; }
        .live-metric-value { margin-top: 4px; font-size: clamp(21px, 2.4vw, 29px); font-weight: 950; line-height: 1; font-variant-numeric: tabular-nums; }
        .live-workspace-main-grid { display: grid; grid-template-columns: minmax(0, 1.38fr) minmax(320px, .72fr); gap: 18px; align-items: start; }
        .live-content-panel { padding: clamp(20px, 2.4vw, 30px); border-radius: 26px; box-shadow: 0 15px 44px ${rgba(shade(theme.primary, 35), theme.light ? .08 : .22)}; }
        .live-section-heading { margin-bottom: 20px; }
        .live-section-eyebrow { margin-bottom: 8px; font-size: 10px; font-weight: 950; letter-spacing: .13em; text-transform: uppercase; }
        .live-section-heading h2 { margin: 0; font-size: clamp(25px, 3.2vw, 38px); line-height: 1.05; letter-spacing: -.025em; }
        .live-section-heading p { max-width: 620px; margin: 8px 0 0; font-size: 14px; line-height: 1.6; }
        .live-member-list,
        .live-group-list { margin: 0; padding: 0; list-style: none; }
        .live-member-list li {
          min-height: 72px;
          display: grid;
          grid-template-columns: 30px 52px minmax(0, 1fr) auto;
          align-items: center;
          gap: 12px;
          padding: 11px 4px;
          border-top: 1px solid;
        }
        .live-member-list li:first-child { border-top: 0; }
        .live-member-list li.is-leading { min-height: 82px; }
        .live-rank-number { font-size: 17px; font-weight: 950; text-align: center; font-variant-numeric: tabular-nums; }
        .live-member-info { min-width: 0; }
        .live-member-identity { display: flex; align-items: baseline; gap: 6px; min-width: 0; flex-wrap: wrap; }
        .live-member-name { overflow: hidden; font-size: 15px; font-weight: 900; text-overflow: ellipsis; white-space: nowrap; }
        .live-member-school-group { display: inline-flex; align-items: center; padding: 3px 8px; border: 1px solid; border-radius: 999px; font-size: 11px; font-weight: 750; white-space: nowrap; }
        .live-member-track,
        .live-group-track,
        .live-challenge-track { height: 5px; overflow: hidden; border-radius: 999px; }
        .live-member-track { max-width: 430px; margin-top: 8px; }
        .live-member-track > div,
        .live-group-track > div,
        .live-challenge-track > div { height: 100%; border-radius: inherit; }
        .live-member-steps { font-size: 14px; font-weight: 950; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .live-member-steps span { font-size: 10px; font-weight: 800; opacity: .58; }
        .live-show-more {
          min-height: 44px;
          margin-top: 16px;
          padding: 9px 14px;
          border: 1px solid;
          border-radius: 999px;
          background: transparent;
          cursor: pointer;
          font: inherit;
          font-size: 13px;
          font-weight: 900;
        }
        .live-side-stack { display: grid; gap: 18px; }
        .live-group-list li { padding: 14px 0; border-top: 1px solid; }
        .live-group-list li:first-child { border-top: 0; padding-top: 4px; }
        .live-group-row { display: grid; grid-template-columns: 24px minmax(0, 1fr) auto; align-items: baseline; gap: 10px; }
        .live-group-rank { font-size: 14px; font-weight: 950; text-align: center; }
        .live-group-name { min-width: 0; display: grid; gap: 2px; }
        .live-group-name > span { font-size: 12px; font-weight: 750; }
        .live-group-row strong { overflow: hidden; font-size: 14px; text-overflow: ellipsis; white-space: nowrap; }
        .live-group-row > span:last-child { font-size: 12px; font-weight: 900; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .live-group-track { margin: 9px 0 0 34px; }
        .live-challenge-list { display: grid; gap: 12px; }
        .live-challenge-card { display: grid; grid-template-columns: 48px minmax(0, 1fr); gap: 12px; padding: 16px; border-radius: 20px; }
        .live-challenge-icon { width: 48px; height: 48px; display: grid; place-items: center; border-radius: 999px; }
        .live-challenge-copy { min-width: 0; }
        .live-challenge-label { margin-bottom: 3px; font-size: 9px; font-weight: 950; letter-spacing: .1em; text-transform: uppercase; }
        .live-challenge-copy strong { display: block; font-size: 15px; line-height: 1.3; }
        .live-challenge-copy span { display: block; margin-top: 5px; font-size: 12px; font-weight: 850; }
        .live-challenge-track { grid-column: 1 / -1; margin-top: 2px; }
        @media (max-width: 860px) {
          .live-workspace-hero,
          .live-workspace-main-grid {
            grid-template-columns: 1fr !important;
          }
          .live-hero-copy, .is-college .live-hero-copy { min-height: auto; }
          .live-hero-visual, .is-college .live-hero-visual { min-height: 340px; }
          .live-stats-strip { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .live-metric:nth-child(3) { border-left: 0; border-top: 1px solid ${theme.borderSoft}; }
          .live-metric:nth-child(4) { border-top: 1px solid ${theme.borderSoft}; }
        }
        @media (max-width: 560px) {
          .live-dashboard-topbar-inner { padding: 8px 12px; }
          .live-back-link { padding: 0 11px; }
          .live-workspace-shell { padding: 14px 10px 72px; }
          .live-workspace-hero { border-radius: 24px; }
          .is-college .live-workspace-hero { border-radius: 14px; }
          .live-hero-copy { padding: 26px 20px 30px; }
          .live-hero-logo { width: 64px; height: 64px; border-radius: 17px; }
          .live-hero-title { font-size: clamp(40px, 13vw, 58px); }
          .live-hero-subtitle { font-size: 15px; }
          .live-hero-facts { display: grid; gap: 6px; }
          .live-hero-facts span + span::before { content: none; }
          .live-featured-people { align-items: flex-start; flex-direction: column; }
          .live-hero-visual, .is-college .live-hero-visual { min-height: 300px; }
          .live-leader-card { left: 14px; right: 14px; bottom: 14px; padding: 14px; border-radius: 18px; }
          .live-stats-strip { border-radius: 18px; }
          .live-metric { min-height: 84px; padding: 12px 10px; gap: 9px; }
          .live-metric-icon { width: 32px; height: 32px; }
          .live-metric-value { font-size: 20px; }
          .live-content-panel { padding: 20px 16px; border-radius: 22px; }
          .live-member-list li { grid-template-columns: 22px 44px minmax(0, 1fr) auto; gap: 9px; }
          .live-member-list li.is-leading { min-height: 74px; }
          .live-member-steps span { display: none; }
          .live-member-track { margin-top: 6px; }
          .live-group-row { grid-template-columns: 20px minmax(0, 1fr); }
          .live-group-row > span:last-child { grid-column: 2; }
          .live-group-track { margin-left: 30px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .live-dashboard-page * { scroll-behavior: auto !important; }
        }
      `}</style>
    </main></MotionConfig>
  );
}
